<template>
    <div class="scale-slider" :class="{ 'is-answered': isAnswered, 'is-dragging': dragFraction !== null, 'is-disabled': disabled }">
        <div
            ref="control"
            class="control"
            role="slider"
            tabindex="0"
            :aria-label="label"
            :aria-describedby="rangeDescription ? rangeId : undefined"
            :aria-valuemin="options[0]?.value"
            :aria-valuemax="options[lastIndex]?.value"
            :aria-valuenow="ariaValueNow"
            :aria-valuetext="isAnswered ? options[displayIndex]?.label : t('components.scaleSlider.notAnswered')"
            :aria-disabled="disabled || undefined"
            @pointerdown="handlePointerDown"
            @pointermove="handlePointerMove"
            @pointerup="handlePointerUp"
            @pointercancel="handlePointerCancel"
            @keydown="handleKeydown">
            <span class="track" />
            <span class="range" :style="{ '--position': thumbFraction }" />
            <span
                v-for="(point, index) in options"
                :key="point.value"
                class="dot"
                :class="{ 'is-filled': isAnswered && index / lastIndex <= thumbFraction, 'is-covered': isAnswered && index === displayIndex }"
                :style="{ '--position': index / lastIndex }" />
            <span class="thumb" :style="{ '--position': thumbFraction }" />
        </div>
        <span v-if="rangeDescription" :id="rangeId" class="sr-only">{{ rangeDescription }}</span>
        <div ref="labels" class="labels">
            <span ref="startLabel" class="end is-start" :class="{ 'is-replaced': overlapsStart }">{{ labelStart }}</span>
            <span ref="endLabel" class="end is-end" :class="{ 'is-replaced': overlapsEnd }">{{ labelEnd }}</span>
            <span ref="valueLabel" class="value" :class="valueAlignment" :style="{ '--position': displayIndex / lastIndex }" aria-hidden="true">
                <span v-if="isAnswered" :key="displayIndex" class="value-text">{{ options[displayIndex]?.label }}</span>
            </span>
            <span class="hint" aria-hidden="true">{{ t("components.scaleSlider.hint") }}</span>
        </div>
    </div>
</template>

<script lang="ts">
export interface ScaleOption {
    value: number;
    label: string;
}

export interface Props {
    /** The points in order from the start anchor to the end anchor. */
    options: readonly ScaleOption[];
    modelValue?: number;
    disabled?: boolean;
    /** The question, used as the slider's accessible name. */
    label?: string;
    labelStart?: string;
    labelEnd?: string;
}
</script>

<script setup lang="ts">
/* eslint design-system/no-off-scale-spacing: "off" -- The 6px inset is half the track height, which keeps the end dots and thumb inside the track's rounded caps. */
/**
 * A rating scale as a snapping slider. A drag follows the pointer and settles on the nearest point
 * when released, so a save is sent per answer rather than per pointer move. It starts with no thumb
 * at all, so an unanswered scale never reads as its middle point. The `confirm` event lets a survey
 * move on once an answer is committed.
 *
 * reka-ui's Slider has no unanswered state and moves its value on every pointer move, so this is a
 * hand-built `role="slider"`.
 */
import { useElementSize } from "@vueuse/core";
import { computed, nextTick, ref, useId, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const { options, modelValue, disabled = false, labelStart, labelEnd } = defineProps<Props>();

const lastIndex = computed(() => options.length - 1);
const INSET_PX = 6;
const LABEL_GAP_PX = 8;
/** Where the first arrow key lands on an unanswered scale. */
const middleIndex = computed(() => Math.floor(lastIndex.value / 2));

const { t } = useI18n();

const emit = defineEmits<{
    "update:modelValue": [value: number];
    /** Pointer answers leave focus where it is, so the next question doesn't light up with a keyboard focus ring. */
    confirm: [via: "pointer" | "keyboard"];
}>();

const rangeId = useId();
// The anchors are drawn for sighted users only, so the range is described on the slider itself; otherwise its direction
// can only be found by pressing an arrow key, which answers.
const rangeDescription = computed(() => (labelStart && labelEnd ? t("components.scaleSlider.range", { start: labelStart, end: labelEnd }) : undefined));

const control = useTemplateRef("control");
const { width: labelsWidth } = useElementSize(useTemplateRef("labels"));
const { width: startLabelWidth } = useElementSize(useTemplateRef("startLabel"));
const { width: endLabelWidth } = useElementSize(useTemplateRef("endLabel"));
const { width: valueLabelWidth } = useElementSize(useTemplateRef("valueLabel"));

/** The pointer's position along the track while a drag is in progress, from 0 to 1. */
const dragFraction = ref<number | null>(null);

const answeredIndex = computed(() => options.findIndex((point) => point.value === modelValue));
const isAnswered = computed(() => dragFraction.value !== null || answeredIndex.value >= 0);
const displayIndex = computed(() => (dragFraction.value === null ? Math.max(answeredIndex.value, 0) : Math.round(dragFraction.value * lastIndex.value)));
const thumbFraction = computed(() => dragFraction.value ?? (answeredIndex.value >= 0 ? answeredIndex.value / lastIndex.value : 0.5));

const valueAlignment = computed(() => {
    if (displayIndex.value === 0) {
        return "is-start";
    }
    return displayIndex.value === lastIndex.value ? "is-end" : "is-middle";
});

// The value label is placed from its destination rather than its animated position, so an end label hides as soon as
// the value label heads toward it instead of after the slide animation lands.
const valueLabelStart = computed(() => {
    if (valueAlignment.value === "is-start") {
        return 0;
    }
    if (valueAlignment.value === "is-end") {
        return labelsWidth.value - valueLabelWidth.value;
    }
    const center = INSET_PX + (labelsWidth.value - INSET_PX * 2) * (displayIndex.value / lastIndex.value);
    return center - valueLabelWidth.value / 2;
});
// An anchor can share its words with a point (an agreement scale's "Agree"), and answering that point would show the word twice.
const answerLabel = computed(() => (isAnswered.value ? options[displayIndex.value]?.label : undefined));
const overlapsStart = computed(() => isAnswered.value && (answerLabel.value === labelStart || valueLabelStart.value < startLabelWidth.value + LABEL_GAP_PX));
const overlapsEnd = computed(
    () => isAnswered.value && (answerLabel.value === labelEnd || valueLabelStart.value + valueLabelWidth.value > labelsWidth.value - endLabelWidth.value - LABEL_GAP_PX),
);

// aria-valuenow is required on a slider, so an unanswered one reports the midpoint and says "Not answered" in its value text.
const ariaValueNow = computed(() => options[isAnswered.value ? displayIndex.value : middleIndex.value]!.value);

function commit(index: number) {
    const value = options[index]!.value;
    if (value !== modelValue) {
        emit("update:modelValue", value);
    }
}

function fractionAt(event: PointerEvent): number {
    const rect = control.value!.getBoundingClientRect();
    const usable = rect.width - INSET_PX * 2;
    return usable > 0 ? Math.min(1, Math.max(0, (event.clientX - rect.left - INSET_PX) / usable)) : 0.5;
}

function handlePointerDown(event: PointerEvent) {
    if (disabled || event.button !== 0) {
        return;
    }
    control.value!.setPointerCapture?.(event.pointerId);
    dragFraction.value = fractionAt(event);
}

function handlePointerMove(event: PointerEvent) {
    if (dragFraction.value !== null) {
        dragFraction.value = fractionAt(event);
    }
}

function handlePointerUp(event: PointerEvent) {
    if (dragFraction.value === null) {
        return;
    }
    // The release can land past the last move event, so it is measured again rather than read from the drag.
    const index = Math.round(fractionAt(event) * lastIndex.value);
    dragFraction.value = null;
    commit(index);
    nextTick(() => emit("confirm", "pointer"));
}

function handlePointerCancel() {
    dragFraction.value = null;
}

function handleKeydown(event: KeyboardEvent) {
    if (disabled) {
        return;
    }
    const current = answeredIndex.value;
    let next: number;
    switch (event.key) {
        case "ArrowRight":
        case "ArrowUp":
            next = current < 0 ? middleIndex.value : Math.min(lastIndex.value, current + 1);
            break;
        case "ArrowLeft":
        case "ArrowDown":
            next = current < 0 ? middleIndex.value : Math.max(0, current - 1);
            break;
        case "Home":
            next = 0;
            break;
        case "End":
            next = lastIndex.value;
            break;
        case "Enter":
        case " ":
            event.preventDefault();
            if (current >= 0) {
                emit("confirm", "keyboard");
            }
            return;
        default:
            return;
    }
    event.preventDefault();
    commit(next);
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .scale-slider {
        --scale-slider-spring: cubic-bezier(0.34, 1.56, 0.64, 1);
        --scale-slider-inset: 6px;
        @apply w-full;
    }

    .scale-slider.is-disabled {
        @apply opacity-60;
    }

    .control {
        @apply relative h-7 cursor-pointer touch-none select-none outline-none rounded-full;
    }

    .is-disabled .control {
        @apply cursor-not-allowed;
    }

    /* Same focus outline as the radio group's scale, which keeps 3:1 against the page whether or not there is an answer yet. */
    .control:focus-visible {
        outline: 2px solid var(--color-control-accent);
        outline-offset: 2px;
    }

    .track,
    .range {
        @apply absolute top-2 left-0 h-3 rounded-full;
    }

    .track {
        @apply right-0 bg-scale-slider-track;
    }

    .range {
        @apply bg-scale-slider-fill opacity-0;
        width: calc(var(--scale-slider-inset) * 2 + (100% - var(--scale-slider-inset) * 2) * var(--position));
        transition:
            width 350ms var(--scale-slider-spring),
            opacity var(--motion-duration-normal) var(--motion-ease-standard);
    }

    .is-answered .range {
        @apply opacity-100;
    }

    .dot {
        @apply absolute top-2.5 size-2 -ml-1 rounded-full bg-scale-slider-dot;
        left: calc(var(--scale-slider-inset) + (100% - var(--scale-slider-inset) * 2) * var(--position));
        transition:
            background-color var(--motion-duration-moderate) var(--motion-ease-standard),
            scale var(--motion-duration-moderate) var(--scale-slider-spring);
    }

    .dot.is-filled {
        @apply bg-scale-slider-dot-filled;
    }

    .dot.is-covered {
        @apply scale-0;
    }

    .thumb {
        @apply absolute top-1 size-5 -ml-2.5 rounded-full border-3 border-scale-slider-fill bg-scale-slider-thumb shadow-scale-slider-thumb scale-0 opacity-0;
        left: calc(var(--scale-slider-inset) + (100% - var(--scale-slider-inset) * 2) * var(--position));
        transition:
            left 350ms var(--scale-slider-spring),
            scale var(--motion-duration-normal) var(--scale-slider-spring),
            opacity var(--motion-duration-normal) var(--motion-ease-standard),
            box-shadow var(--motion-duration-normal) var(--motion-ease-standard);
    }

    .is-answered .thumb {
        @apply scale-100 opacity-100;
    }

    .is-answered:not(.is-disabled) .control:hover .thumb {
        @apply ring-4 ring-scale-slider-fill/10;
    }

    .is-dragging .thumb {
        @apply scale-110 ring-4 ring-scale-slider-fill/15;
    }

    .is-dragging .thumb,
    .is-dragging .range {
        transition-duration: 0s;
    }

    .labels {
        @apply relative mt-2.5 h-[18px] scaffold-details-small text-ink-muted select-none;
    }

    .labels > span {
        @apply absolute top-0 whitespace-nowrap;
    }

    .end {
        transition: opacity var(--motion-duration-normal) var(--motion-ease-standard);
    }

    .end.is-start {
        @apply left-0;
    }

    .end.is-end {
        @apply right-0;
    }

    .end.is-replaced {
        @apply opacity-0;
    }

    .value {
        @apply scaffold-details-bold text-scale-slider-fill;
        left: calc(var(--scale-slider-inset) + (100% - var(--scale-slider-inset) * 2) * var(--position));
        transition:
            left 350ms var(--scale-slider-spring),
            translate 350ms var(--motion-ease-standard);
    }

    .value.is-start {
        @apply -translate-x-1.5;
    }

    .value.is-middle {
        @apply -translate-x-1/2;
    }

    .value.is-end {
        @apply -translate-x-[calc(100%-6px)];
    }

    .is-dragging .value {
        transition-duration: 0s;
    }

    .value-text {
        animation: scale-slider-value-in var(--motion-duration-moderate) var(--motion-ease-enter);
    }

    .hint {
        @apply left-1/2 -translate-x-1/2;
        transition: opacity var(--motion-duration-normal) var(--motion-ease-standard);
    }

    .is-answered .hint {
        @apply opacity-0;
    }

    @keyframes scale-slider-value-in {
        from {
            opacity: 0.3;
            translate: 0 3px;
        }
        to {
            opacity: 1;
            translate: 0 0;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .scale-slider * {
            transition: none !important;
            animation: none !important;
        }
    }
}
</style>
